/**
 * useLocalStorage Hook
 * Provides persistent state storage using localStorage with automatic serialization
 */

import { useState, useEffect, useCallback } from 'react';
import { STORAGE_CONFIG } from '@/constants';

// ============================================================
// TYPES
// ============================================================

/** Options for the useLocalStorage hook */
export interface UseLocalStorageOptions<T> {
  /** Custom serializer function */
  serializer?: (value: T) => string;
  /** Custom deserializer function */
  deserializer?: (value: string) => T;
  /** Whether to sync across browser tabs */
  syncTabs?: boolean;
}

/** Return type for the useLocalStorage hook */
export type UseLocalStorageReturn<T> = [
  /** Current value */
  T,
  /** Setter function */
  (value: T | ((prev: T) => T)) => void,
  /** Remove value from storage */
  () => void
];

// ============================================================
// STORAGE UTILITIES
// ============================================================

/**
 * Get the full storage key with prefix
 */
export function getStorageKey(key: string): string {
  return `${STORAGE_CONFIG.prefix}${key}`;
}

/**
 * Safely parse JSON with error handling
 */
export function safeJsonParse<T>(value: string | null, fallback: T): T {
  if (value === null) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

/**
 * Safely stringify JSON with error handling
 */
export function safeJsonStringify<T>(value: T): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    console.error('Failed to serialize value for localStorage');
    return null;
  }
}

// ============================================================
// MAIN HOOK
// ============================================================

/**
 * Hook for managing state that persists in localStorage
 * 
 * @param key - Storage key (prefix will be added automatically)
 * @param initialValue - Initial value if nothing is stored
 * @param options - Optional configuration
 * @returns [value, setValue, removeValue]
 * 
 * @example
 * ```tsx
 * const [nodes, setNodes, removeNodes] = useLocalStorage('nodes', []);
 * 
 * // Update nodes
 * setNodes([...nodes, newNode]);
 * 
 * // Clear stored nodes
 * removeNodes();
 * ```
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  options: UseLocalStorageOptions<T> = {}
): UseLocalStorageReturn<T> {
  const {
    serializer = safeJsonStringify,
    deserializer = (v) => safeJsonParse(v, initialValue),
    syncTabs = false,
  } = options;

  const storageKey = getStorageKey(key);

  // Initialize state from localStorage
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(storageKey);
      return item !== null ? deserializer(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${storageKey}":`, error);
      return initialValue;
    }
  });

  // Update localStorage when state changes
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        // Allow value to be a function for same API as useState
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        
        setStoredValue(valueToStore);

        if (typeof window !== 'undefined') {
          const serialized = serializer(valueToStore);
          if (serialized !== null) {
            window.localStorage.setItem(storageKey, serialized);
          }
        }
      } catch (error) {
        console.error(`Error setting localStorage key "${storageKey}":`, error);
      }
    },
    [storageKey, storedValue, serializer]
  );

  // Remove value from localStorage
  const removeValue = useCallback(() => {
    try {
      setStoredValue(initialValue);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.error(`Error removing localStorage key "${storageKey}":`, error);
    }
  }, [storageKey, initialValue]);

  // Sync across tabs if enabled
  useEffect(() => {
    if (!syncTabs || typeof window === 'undefined') return;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== storageKey) return;
      try {
        if (event.newValue === null) {
          setStoredValue(initialValue);
        } else {
          setStoredValue(deserializer(event.newValue));
        }
      } catch (error) {
        console.error(`Error syncing localStorage key "${storageKey}":`, error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey, deserializer, syncTabs, initialValue]);

  return [storedValue, setValue, removeValue];
}

// ============================================================
// ADDITIONAL UTILITIES
// ============================================================

/**
 * Simple storage object for direct access (legacy compatibility)
 * Use useLocalStorage hook for React components instead
 */
export const storage = {
  /**
   * Save a value to localStorage
   */
  save: <T>(key: string, value: T): void => {
    try {
      const storageKey = getStorageKey(key);
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving to localStorage:`, error);
    }
  },

  /**
   * Load a value from localStorage
   */
  load: <T>(key: string): T | null => {
    try {
      const storageKey = getStorageKey(key);
      const item = localStorage.getItem(storageKey);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Error loading from localStorage:`, error);
      return null;
    }
  },

  /**
   * Remove a value from localStorage
   */
  remove: (key: string): void => {
    try {
      const storageKey = getStorageKey(key);
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`Error removing from localStorage:`, error);
    }
  },

  /**
   * Clear all app-specific localStorage items
   */
  clear: (): void => {
    try {
      const prefix = STORAGE_CONFIG.prefix;
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error(`Error clearing localStorage:`, error);
    }
  },

  /**
   * Get all stored keys (without prefix)
   */
  keys: (): string[] => {
    try {
      const prefix = STORAGE_CONFIG.prefix;
      const keys: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix)) {
          keys.push(key.slice(prefix.length));
        }
      }
      
      return keys;
    } catch (error) {
      console.error(`Error getting localStorage keys:`, error);
      return [];
    }
  },
};

// ============================================================
// SPECIALIZED HOOKS
// ============================================================

/**
 * Hook specifically for persisting network nodes
 */
export function usePersistedNodes<T>(initialValue: T[] = [] as T[]) {
  return useLocalStorage<T[]>('nodes', initialValue);
}

/**
 * Hook specifically for persisting network connections
 */
export function usePersistedConnections<T>(initialValue: T[] = [] as T[]) {
  return useLocalStorage<T[]>('connections', initialValue);
}

export default useLocalStorage;
